import { useCallback, useState } from 'react';
import type { CoreMembership, CoreOrganization, CoreRole, UserLabel } from '@/lib/zifekCore';
import { createCoreMembership, searchUsers, updateCoreMembership } from '@/lib/zifekCore';
import { roleMeta } from '@/lib/corePermissions';
import Modal from './Modal';

interface Props {
  org: CoreOrganization;
  memberships: CoreMembership[];
  roles: CoreRole[];
  users: Record<number, UserLabel>;
  onChanged: () => void;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function MembersManager({ org, memberships, roles, users, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserLabel[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<UserLabel | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const defaultRole = roles.find((r) => r.role_key === 'employee') || roles[0];

  const runSearch = useCallback(async (q: string) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const found = await searchUsers(q);
      // Exclure ceux déjà membres
      const memberIds = new Set(memberships.map((m) => m.user_id));
      setResults(found.filter((u) => !memberIds.has(u.id)));
    } finally {
      setSearching(false);
    }
  }, [memberships]);

  const openAdd = () => {
    setQuery('');
    setResults([]);
    setPicked(null);
    setRoleId(defaultRole?.id ?? null);
    setError('');
    setOpen(true);
  };

  const save = async () => {
    if (!picked) { setError('Sélectionnez un utilisateur.'); return; }
    const role = roles.find((r) => r.id === roleId) || defaultRole;
    if (!role) { setError('Aucun rôle disponible.'); return; }
    setSaving(true);
    setError('');
    try {
      await createCoreMembership({
        tenantId: org.tenant_id,
        organizationId: org.id,
        userId: picked.id,
        roleKey: role.role_key,
        roleLabel: role.name,
        roleId: role.id,
        permissions: role.permissions || [],
      });
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ajout impossible (la personne est peut-être déjà membre).');
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (m: CoreMembership, newRoleId: number) => {
    const role = roles.find((r) => r.id === newRoleId);
    if (!role) return;
    setBusyId(m.id);
    try {
      await updateCoreMembership(m.id, {
        roleKey: role.role_key,
        roleLabel: role.name,
        roleId: role.id,
        permissions: role.permissions || [],
      });
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const toggleStatus = async (m: CoreMembership) => {
    setBusyId(m.id);
    try {
      await updateCoreMembership(m.id, { status: m.status === 'active' ? 'suspended' : 'active' });
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          Les personnes rattachées à <strong className="text-foreground-700">{org.commercial_name || org.legal_name}</strong> et leur rôle.
        </p>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-user-add-line"></i>
          Ajouter un membre
        </button>
      </div>

      {memberships.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-group-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun membre rattaché.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-background-100 text-left text-xs text-foreground-500">
                  <th className="px-4 py-3 font-medium">Membre</th>
                  <th className="px-4 py-3 font-medium">Rôle</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((m) => {
                  const u = users[m.user_id];
                  const meta = roleMeta(m.role, m.role_label);
                  return (
                    <tr key={m.id} className="border-t border-background-200/60">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {(u?.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-foreground-900 truncate">
                              {u?.name || `Utilisateur #${m.user_id}`}
                              {m.is_primary && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-medium">Propriétaire</span>}
                            </div>
                            <div className="text-xs text-foreground-400 truncate">
                              {u?.user_name ? `@${u.user_name}` : ''}{u?.email ? ` · ${u.email}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {m.is_primary ? (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.className}`}>{meta.label}</span>
                        ) : (
                          <select
                            value={m.role_id ?? ''}
                            onChange={(e) => changeRole(m, Number(e.target.value))}
                            disabled={busyId === m.id}
                            className="px-2.5 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer disabled:opacity-50"
                          >
                            {m.role_id == null && <option value="">{m.role_label || m.role}</option>}
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.status === 'active' ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                          {m.status === 'active' ? 'Actif' : 'Suspendu'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!m.is_primary && (
                          <button
                            onClick={() => toggleStatus(m)}
                            disabled={busyId === m.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50"
                          >
                            <i className={m.status === 'active' ? 'ri-user-unfollow-line' : 'ri-user-follow-line'}></i>
                            {m.status === 'active' ? 'Suspendre' : 'Réactiver'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <Modal
          title="Ajouter un membre"
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving || !picked}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-user-add-line"></i>}
                Rattacher
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
          <div className="space-y-3">
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Rechercher un compte Zifek</span>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                <input
                  className={`${INPUT} pl-9`}
                  value={query}
                  onChange={(e) => runSearch(e.target.value)}
                  placeholder="Nom, pseudo ou email…"
                />
              </div>
            </div>

            {searching && (
              <div className="flex items-center justify-center py-4"><i className="ri-loader-4-line animate-spin text-primary-500"></i></div>
            )}

            {!searching && results.length > 0 && (
              <div className="border border-background-200/70 rounded-md divide-y divide-background-200/60 max-h-52 overflow-y-auto">
                {results.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setPicked(u)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors cursor-pointer ${picked?.id === u.id ? 'bg-primary-50' : 'hover:bg-background-100'}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-xs font-bold text-foreground-600 flex-shrink-0">
                      {(u.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground-900 truncate">{u.name || `#${u.id}`}</div>
                      <div className="text-xs text-foreground-400 truncate">{u.user_name ? `@${u.user_name}` : ''}{u.email ? ` · ${u.email}` : ''}</div>
                    </div>
                    {picked?.id === u.id && <i className="ri-check-line text-primary-600 ml-auto"></i>}
                  </button>
                ))}
              </div>
            )}

            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <p className="text-xs text-foreground-400">Aucun utilisateur trouvé (ou déjà membre).</p>
            )}

            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Rôle</span>
              <select
                className={INPUT}
                value={roleId ?? ''}
                onChange={(e) => setRoleId(Number(e.target.value))}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}